import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { requestPushPermission, getPermissionStatus, isPushSupported } from '../utils/pushNotificationService';
import { useAuth } from '../contexts/AuthContext';

/**
 * NotificationPermissionPrompt Component
 * Proactively asks for notification permission in any supported browser
 */
const NotificationPermissionPrompt = () => {
    const { user } = useAuth();
    const [isVisible, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Only run for logged-in users tracking their goals
        if (!user) return;

        const checkPromptEligibility = () => {
            // Only prompt if push notifications are supported
            if (!isPushSupported()) return;

            // Only prompt if permission is still at default
            const status = getPermissionStatus();
            if (status !== 'default') return;

            // Check if we already prompted in this session or recently
            const lastPrompted = localStorage.getItem('notif_prompt_last_v2');
            const now = Date.now();

            // Show if never prompted, or if it's been more than 3 days since last prompt
            if (!lastPrompted || (now - parseInt(lastPrompted)) > (3 * 24 * 60 * 60 * 1000)) {
                // Show after a short delay on launch
                const timer = setTimeout(() => {
                    setIsVisible(true);
                }, 3000);
                return () => clearTimeout(timer);
            }
        };

        checkPromptEligibility();
    }, [user]);

    const handleEnable = async () => {
        setLoading(true);
        try {
            const result = await requestPushPermission(user.id);
            if (result.success) {
                setIsVisible(false);
            }
        } catch (error) {
            console.error('Error enabling notifications:', error);
        } finally {
            setLoading(false);
            localStorage.setItem('notif_prompt_last_v2', Date.now().toString());
        }
    };

    const handleLater = () => {
        setIsVisible(false);
        localStorage.setItem('notif_prompt_last_v2', Date.now().toString());
    };

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            {isVisible && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10000]"
                        onClick={handleLater}
                    />

                    {/* Prompt Modal */}
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:bottom-8 md:w-[400px]
                               bg-white dark:bg-[#161b22] rounded-3xl shadow-2xl z-[10001] overflow-hidden border border-gray-100 dark:border-[#30363d]"
                    >
                        {/* Close Button */}
                        <button
                            onClick={handleLater}
                            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-[#21262d] transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        </button>

                        <div className="p-8">
                            {/* Icon & App Identity */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-indigo-900/30">
                                    <BellIcon className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
                                        Enable Notifications
                                    </h3>
                                    <p className="text-xs font-medium text-jtc-violet uppercase tracking-wider">
                                        Stay in the loop
                                    </p>
                                </div>
                            </div>

                            {/* Value Proposition */}
                            <div className="space-y-4 mb-8">
                                <div className="flex gap-3">
                                    <div className="w-6 h-6 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Know when your focus sessions complete, even in the background.
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Get notified when a partner invites you.
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <div className="w-6 h-6 rounded-full bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Celebrate level-ups, records, and streak shields instantly.
                                    </p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col gap-3">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={handleEnable}
                                    disabled={loading}
                                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white
                                             font-bold rounded-2xl shadow-xl shadow-indigo-100 dark:shadow-indigo-900/20 flex items-center justify-center gap-2
                                             disabled:opacity-70"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <ShieldCheckIcon className="w-5 h-5" />
                                            Enable Push Notifications
                                        </>
                                    )}
                                </motion.button>

                                <button
                                    onClick={handleLater}
                                    className="w-full py-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors text-center"
                                >
                                    Maybe later
                                </button>
                            </div>

                            {/* Privacy Reassurance */}
                            <p className="mt-6 text-[10px] text-gray-400 dark:text-gray-500 text-center leading-relaxed">
                                We only send notifications for your goals and shared activity.
                                You can change this anytime in Settings.
                            </p>
                        </div>

                        {/* Tech Accent */}
                        <div className="h-1 bg-gradient-to-r from-transparent via-jtc-violet/20 to-transparent" />
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default NotificationPermissionPrompt;
